import {
    Dialog,
    DialogContent,
    Box,
    Typography,
    IconButton,
    CircularProgress,
} from "@mui/material";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import CloseIcon from "@mui/icons-material/Close";
import React, { useEffect, useRef, useState } from "react";

interface Props {
    open: boolean;
    onClose: () => void;
    text: string;
}

const AISummaryDialog: React.FC<Props> = ({
    open,
    onClose,
    text,
}) => {
    const [displayedText, setDisplayedText] = useState("");
    const [isTyping, setIsTyping] = useState(false);
    const [isGenerating, setIsGenerating] = useState(false);

    const wordsRef = useRef<string[]>([]);
    const indexRef = useRef(0);
    const containerRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        if (!open) return;

        setDisplayedText("");
        setIsTyping(false);
        setIsGenerating(true);
        indexRef.current = 0;

        let startTime = Date.now();
        let animationFrameId: number;

        const thinkingTimeout = setTimeout(() => {
            setIsGenerating(false);
            setIsTyping(true);
            wordsRef.current = text.split(" ");
            const speed = 80;
            const animate = () => {
                const elapsed = Date.now() - startTime;
                const wordsToShow = Math.min(
                    Math.floor(elapsed / speed),
                    wordsRef.current.length
                );

                if (wordsToShow !== indexRef.current) {
                    indexRef.current = wordsToShow;

                    const newText = wordsRef.current
                        .slice(0, wordsToShow)
                        .join(" ");

                    setDisplayedText(newText);
                    if (containerRef.current) {
                        containerRef.current.scrollTop =
                            containerRef.current.scrollHeight;
                    }
                }
                if (wordsToShow < wordsRef.current.length) {
                    animationFrameId = requestAnimationFrame(animate);
                } else {
                    setIsTyping(false);
                }
            };
            animationFrameId = requestAnimationFrame(animate);
        }, 600);

        return () => {
            clearTimeout(thinkingTimeout);
            cancelAnimationFrame(animationFrameId);
        };
    }, [open, text]);

    return (
        <>
            <Dialog
                open={open}
                onClose={onClose}
                fullWidth
                maxWidth="sm"
                slotProps={{
                    backdrop: {
                        sx: {
                            backgroundColor: "rgba(0,0,0,0)",
                        },
                    },
                    paper: {
                        sx: {
                            borderRadius: "20px",
                            maxHeight: "45vh",
                            margin: 0,
                        },
                    },
                }}
                sx={{
                    "& .MuiDialog-container": {
                        justifyContent: "flex-end",
                        alignItems: "flex-start",
                        pr: "3rem",
                        pt: "9rem",
                    },
                }}
            >
                <DialogContent
                    sx={{
                        p: 0,
                        display: "flex",
                        flexDirection: "column",
                    }}
                >
                    {/* Header */}
                    <Box
                        sx={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                            p: 2,
                            pb: 1,
                        }}
                    >
                        <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
                            <AutoAwesomeIcon color="primary" />
                            <Typography fontWeight={600}>
                                AI Summary
                            </Typography>
                        </Box>

                        <IconButton size="small" onClick={onClose}>
                            <CloseIcon />
                        </IconButton>
                    </Box>

                    {/* Subheading */}
                    <Typography
                        sx={{
                            px: 2,
                            pb: 1,
                            fontSize: "12px",
                            color: "text.secondary",
                            letterSpacing: "1px",
                            textTransform: "uppercase",
                        }}
                    >
                        Customer Timeline Analysis
                    </Typography>

                    {/* Content */}
                    <Box
                        ref={containerRef}
                        sx={{
                            px: 2,
                            pb: 2,
                            overflowY: "auto",
                        }}
                    >
                        {/* Generating */}
                        {isGenerating && (
                            <Box
                                sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 1,
                                    mb: 2,
                                }}
                            >
                                <CircularProgress size={16} />
                                <Typography variant="body2">
                                    Generating summary...
                                </Typography>
                            </Box>
                        )}

                        {/* AI Text */}
                        <Typography
                            sx={{
                                lineHeight: 1.8,
                                fontSize: "15px",
                                color: "#444",
                                whiteSpace: "pre-line",
                            }}
                        >
                            {displayedText}
                            {isTyping && <span className="cursor">|</span>}
                        </Typography>
                    </Box>
                </DialogContent>
            </Dialog>

            {/* Cursor */}
            <style>
                {`
          .cursor {
            margin-left: 2px;
            animation: blink 1s infinite;
          }

          @keyframes blink {
            0%, 50%, 100% { opacity: 1; }
            25%, 75% { opacity: 0; }
          }
        `}
            </style>
        </>
    );
};

export default AISummaryDialog;